import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { plannerProgramsThunks } from '@ucheba/store/planner/programs'
import { probabilitiesSelectors, probabilitiesThunks } from '@ucheba/store/probabilities'
import { profileSelectors } from '@ucheba/store/profile'
import { authSelectors } from '@ucheba/store/auth'

interface IUseOddsAssessmentResultsCore {
  (
    programId: number,
    programIds: number[]
  ): {
    onDeleteProgram: (id: number) => void
    onAddProgram: (id: number) => void
    hasInPlan: boolean
    isLoading: boolean
  }
}

export const useOddsAssessmentResultsCore: IUseOddsAssessmentResultsCore = (
  programId,
  programIds
) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const anotherPrograms = useSelector(probabilitiesSelectors.anotherPrograms)
  const profile = useSelector(profileSelectors.entity)
  const currentProbability = useSelector(probabilitiesSelectors.currentProbability)
  // сделан ли запрос
  const [didSubmit, setDidSubmit] = useState(false)
  const isAuth = useSelector(authSelectors.isAuth)

  // есть ли оценка шансов
  const hasCurrentProbability = useMemo(() => {
    if (!isAuth) return false

    return !!currentProbability
  }, [currentProbability, isAuth])

  const hasInPlan = useMemo(() => {
    return programIds.includes(programId)
  }, [programId, programIds])

  const onAddProgram = useCallback(
    async (id: number) => {
      if (programId === id) {
        setIsLoading(true)
      }

      await dispatch(
        plannerProgramsThunks.postPrograms({
          data: {
            entityIds: [id],
          },
        })
      )

      await dispatch(plannerProgramsThunks.getProgramIds({}))

      if (programId === id) {
        setIsLoading(false)
      }
    },
    [dispatch, programId]
  )

  const onDeleteProgram = useCallback(
    async (id: number) => {
      if (programId === id) {
        setIsLoading(true)
      }

      await dispatch(
        plannerProgramsThunks.deletePrograms({
          data: {
            entityIds: [id],
          },
        })
      )

      await dispatch(plannerProgramsThunks.getProgramIds({}))

      if (programId === id) {
        setIsLoading(false)
      }
    },
    [dispatch, programId]
  )

  const exams = useMemo(() => {
    return (
      profile?.enrolleeInfo?.exams?.map((exam) => ({
        examId: exam.exam.id,
        score: exam.score,
      })) || []
    )
  }, [profile])

  const achievements = useMemo(() => {
    return profile?.enrolleeInfo?.achievements || []
  }, [profile])

  // при загрузке страницы делаем пост запрос чтоб получить программы в оценке шансов
  const handleSubmit = useCallback(async () => {
    if (hasCurrentProbability && profile && !didSubmit) {
      await dispatch(
        probabilitiesThunks.post({
          data: {
            type: 'program',
            referId: programId,
            scores: exams,
            achievementIds: achievements.map((el) => el.id),
            readyToExam: true,
          },
        })
      )

      setDidSubmit(true)
    }
  }, [
    hasCurrentProbability,
    achievements,
    exams,
    programId,
    dispatch,
    profile,
    didSubmit,
  ])

  useEffect(() => {
    if ((!anotherPrograms || anotherPrograms.length === 0) && profile) {
      handleSubmit()
    }
  }, [profile])

  return {
    onAddProgram,
    onDeleteProgram,
    hasInPlan,
    isLoading,
  }
}
